import React from 'react';
import { LoadingSpinner } from './LoadingSpinner';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  children,
  type = 'button',
  onClick,
  className = '',
}) => {
  const baseClasses =
    'font-semibold rounded-xl transition-all duration-200 ease-out flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98]';

  // Primary is the BRAND PLUM (#6D28A8), which the Orchid design system names for
  // "brand, primary buttons, active nav" and which the `sage`/`primary` keys both
  // resolve to. Three colours used to mean "primary" — this indigo, a hand-rolled
  // bright blue on the Care Pass and caregiver screens, and near-black on the prep
  // sheet — and only one of the three was ever a token. Secondary (outlined) and
  // ghost (text only) pick up the same plum accent so the system reads as one
  // family; danger stays red and is reserved for destructive confirmation.
  // See DECISIONS.md §DS-2.
  const variantClasses = {
    primary:
      'bg-sage-600 text-white hover:bg-sage-700 shadow-soft hover:shadow-card focus-visible:ring-sage-500',
    secondary:
      'border border-faint text-charcoal bg-white hover:bg-sage-50 hover:border-sage-200 shadow-xs focus-visible:ring-sage-400',
    danger:
      'bg-danger-600 text-white hover:bg-danger-700 shadow-soft hover:shadow-card focus-visible:ring-danger-500',
    ghost:
      'text-mid hover:text-charcoal hover:bg-sage-50 focus-visible:ring-sage-400',
  };

  const sizeClasses = {
    sm: 'px-3.5 py-2 text-sm min-h-[36px]',
    md: 'px-5 py-2.5 text-sm min-h-[44px]',
    lg: 'px-6 py-3 text-base min-h-[48px]',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  const combinedClasses =
    `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`.trim();

  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      onClick={onClick}
      className={combinedClasses}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <>
          <LoadingSpinner size="sm" />
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
};
